import { MoreOutlined } from '@ant-design/icons';
import { Popover, Tag } from 'antd';
import { useTranslate } from '@tolgee/react';
import type { Condition } from 'views/PureLLM/Types/api';

export interface TriggerCondition {
  label: string;
  value?: string;
  operator?: 'and' | 'or';
  groupIndex?: number;
  groupOperator?: 'and' | 'or';
  conditionOperator?: Condition['operator'];
  expectList?: string[];
  hide?: boolean;
}

interface TriggerConditionGroup {
  conditions: TriggerCondition[];
  groupOperator?: 'and' | 'or';
}

interface TriggerConditionListProps {
  conditions: TriggerCondition[];
  operatorDataSid: string;
  itemDataSid: string;
  tagDataSid: string;
  overflowTriggerDataSid: string;
  overflowPopoverDataSid: string;
}

const CONDITION_TAG_STYLE = {
  background: '#F7F9FA',
  border: 'none',
  minHeight: 32,
  padding: '6px 12px',
} as const;

const OPERATOR_TAG_STYLE = {
  background: '#F0F7FF',
  color: '#336FFF',
  fontWeight: 400,
  fontSize: 14,
} as const;

const GROUP_OR_STYLE = {
  background: '#EBFAF3',
  color: '#00BF69',
  fontWeight: 400,
  fontSize: 14,
} as const;

function getConditionValues(condition: TriggerCondition): string[] {
  if (condition.expectList?.length) return condition.expectList;
  if (condition.value) return [condition.value];
  return [];
}

function normalizeConditionValue(value: string, t: (key: string) => string) {
  if (value === 'true' || value === 'YES') return t('common.boolean.true');
  if (value === 'false' || value === 'NO') return t('common.boolean.false');
  return value;
}

function getConditionValueDisplay(
  condition: TriggerCondition,
  t: (key: string) => string,
): {
  inlineText: string;
  overflowValues: string[];
} {
  const values = getConditionValues(condition);
  const normalizedValues = values.map((value) => normalizeConditionValue(value, t));

  switch (condition.conditionOperator) {
    case 'LT':
      return {
        inlineText: normalizedValues[0] ? `< ${normalizedValues[0]}` : '<',
        overflowValues: [],
      };
    case 'GT':
      return {
        inlineText: normalizedValues[0] ? `> ${normalizedValues[0]}` : '>',
        overflowValues: [],
      };
    case 'BETWEEN':
      return {
        inlineText:
          normalizedValues.length >= 2
            ? `${normalizedValues[0]} ~ ${normalizedValues[1]}`
            : normalizedValues.join(' ~ ') || '~',
        overflowValues: [],
      };
    case 'NO_LIMIT':
      return {
        inlineText: t('scene.aiAgentConfig.scenarioDetail.conditionValue.noLimit'),
        overflowValues: [],
      };
    default:
      return {
        inlineText: normalizedValues.slice(0, 3).join(' '),
        overflowValues: normalizedValues.slice(3),
      };
  }
}

function getOperatorLabel(operator?: 'and' | 'or') {
  return operator === 'or'
    ? 'scene.aiAgentConfig.scenarioDetail.conditionOperator.or'
    : 'scene.aiAgentConfig.scenarioDetail.conditionOperator.and';
}

function getOperatorStyle(operator?: 'and' | 'or') {
  return operator === 'or' ? GROUP_OR_STYLE : OPERATOR_TAG_STYLE;
}

function ConditionTag(props: {
  condition: TriggerCondition;
  tagDataSid: string;
  overflowTriggerDataSid: string;
  overflowPopoverDataSid: string;
}) {
  const { t } = useTranslate();
  const { condition, tagDataSid, overflowTriggerDataSid, overflowPopoverDataSid } = props;
  const { inlineText, overflowValues } = getConditionValueDisplay(condition, t);

  return (
    <Tag
      data-sid={tagDataSid}
      className="!mr-0 m-0 inline-flex items-center rounded-[4px] px-3 py-0 text-gray-600"
      style={CONDITION_TAG_STYLE}
    >
      <span className="mr-1 text-[#84868C]">{condition.label}</span>
      <span className="font-medium text-[#232426]">{inlineText}</span>
      {overflowValues.length ? (
        <Popover
          content={
            <div data-sid={overflowPopoverDataSid} className="flex max-w-[320px] flex-wrap gap-2">
              {getConditionValues(condition).map((value, index) => (
                <Tag
                  key={`${value}-${index}`}
                  className="!mr-0 m-0 rounded-[4px] border-0 bg-[#F7F9FA] px-3 py-[7px] text-[#232426]"
                >
                  {normalizeConditionValue(value, t)}
                </Tag>
              ))}
            </div>
          }
          trigger="hover"
          placement="top"
        >
          <button
            data-sid={overflowTriggerDataSid}
            type="button"
            className="ml-2 inline-flex h-4 w-4 cursor-pointer items-center justify-center rounded-full border-0 bg-[#D6E8FF] p-0"
            aria-label={t('scene.aiAgentConfig.scenarioDetail.action.viewMoreConditionValues')}
          >
            <MoreOutlined
              style={{
                color: 'var(--ant-primary-color)',
                display: 'block',
                fontSize: 10,
                lineHeight: 1,
                transform: 'rotate(90deg)',
              }}
            />
          </button>
        </Popover>
      ) : null}
    </Tag>
  );
}

export function groupTriggerConditions(
  triggerConditions: TriggerCondition[],
): TriggerConditionGroup[] {
  if (!triggerConditions.length) return [];

  const groups: TriggerConditionGroup[] = [];

  triggerConditions.forEach((condition) => {
    const normalizedGroupIndex = condition.groupIndex ?? 0;
    const lastGroup = groups[groups.length - 1];

    if (!lastGroup || normalizedGroupIndex !== groups.length - 1) {
      groups.push({
        conditions: [condition],
        groupOperator: condition.groupOperator,
      });
      return;
    }

    lastGroup.conditions.push(condition);
  });

  return groups;
}

function TriggerConditionList(props: TriggerConditionListProps) {
  const { t } = useTranslate();
  const {
    conditions,
    operatorDataSid,
    itemDataSid,
    tagDataSid,
    overflowTriggerDataSid,
    overflowPopoverDataSid,
  } = props;
  const visibleConditions = conditions.filter((c) => !c.hide);
  const triggerConditionGroups = groupTriggerConditions(visibleConditions);

  return (
    <div className="flex flex-1 flex-col gap-2">
      {triggerConditionGroups.map((group, groupIdx) => (
        <div key={groupIdx} className="flex flex-col gap-2">
          {groupIdx > 0 && group.groupOperator && (
            <span
              data-sid={operatorDataSid}
              className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-[4px] text-sm"
              style={getOperatorStyle(group.groupOperator)}
            >
              {t(getOperatorLabel(group.groupOperator))}
            </span>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            {group.conditions.map((condition, conditionIdx) => (
              <div
                data-sid={itemDataSid}
                key={`${groupIdx}-${conditionIdx}`}
                className="flex items-center gap-2"
              >
                {conditionIdx > 0 && (
                  <span
                    data-sid={operatorDataSid}
                    className="inline-flex h-[32px] w-[32px] items-center justify-center rounded-[4px] text-sm"
                    style={OPERATOR_TAG_STYLE}
                  >
                    {t('scene.aiAgentConfig.scenarioDetail.conditionOperator.and')}
                  </span>
                )}
                <ConditionTag
                  condition={condition}
                  tagDataSid={tagDataSid}
                  overflowTriggerDataSid={overflowTriggerDataSid}
                  overflowPopoverDataSid={overflowPopoverDataSid}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default TriggerConditionList;
